/**
 * 운영자 고객 상세의 「월 보고서 이미지」 — 회차를 골라 미리 보고 PNG 로 받아 고객에게 보낸다
 * (261019-monthly-report-card).
 *
 * ★ **운영자 전용이다.** 이미지 경로가 운영자 아니면 404 다. `BrandDetailPage` 가 `isAdmin` 일 때만 마운트한다.
 * ★ 회차 라벨은 서버가 정한다 — 고객 리포트와 같은 `getWorks` 의 `cycle` 을 그대로 보인다(화면이 날짜를 계산하지 않는다).
 * ★ 처음 여는 회차는 **어제가 든 회차**다(서버의 `openBack` — 고객 보고서와 같다, 261020-report-cycle-yesterday-basis).
 *   9/20 구독이면 10/20 에는 막 끝난 9/20~10/19 다. 회차가 아직 진행 중이면(`cycle.inProgress`, 서버가 정한다)
 *   지표가 어제까지라 안내를 띄운다(이미지에는 넣지 않는다).
 */
import { works } from '@geo-admin/shared/contract/api/index.ts';
import { useEffect, useState } from 'react';
import { useApi } from '../lib/api.ts';
import { useCycleBack } from '../lib/works.ts';
import { CyclePicker } from './CyclePicker.tsx';
import { Card, CardBody, CardHeader } from './ui/card.tsx';

/** <img> 가 헤더를 못 실어 쿼리로 운영자 영역을 알린다(middleware/session.ts) */
const cardUrl = (brandId: string, back: number, download: boolean) =>
  `/api/brands/${brandId}/report-card.png?cycle=${back}&realm=admin${download ? '' : '&download=0'}`;

export function BrandReportCardAdmin({ brandId }: { brandId: string }) {
  const { back, param, ready, setBack, learn } = useCycleBack(brandId);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const q = useApi(works.getWorks, { query: { brandId, cycle: param } });
  const cycle = q.data?.cycle;
  useEffect(() => learn(q.data?.openBack), [q.data?.openBack, learn]);
  const go = (next: number) => {
    setState('loading');
    setBack(next);
  };

  return (
    <Card>
      <CardHeader
        title="월 보고서 이미지"
        description="한 회차의 진행 내역·콘텐츠·언급률을 이미지 한 장으로 만듭니다. 받아서 고객에게 보내세요"
        actions={<CyclePicker cycle={cycle} back={back} onBack={go} />}
      />
      <CardBody>
        <div className="flex flex-wrap items-start gap-6">
          <div className="w-full max-w-[360px] overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
            {state === 'error' ? (
              <p className="p-6 text-sm text-bad">이미지를 만들지 못했습니다. 잠시 뒤 다시 시도하세요.</p>
            ) : null}
            {/* 회차를 안 뒤에 그린다 — 이미지는 무겁다. 그 전에 그리면 구독일 당일에 빈 새 회차를 한 번 만든다 */}
            {ready ? (
              <img
                key={back}
                src={cardUrl(brandId, back, false)}
                alt={cycle ? `${cycle.label} 월 보고서 이미지 미리보기` : '월 보고서 이미지 미리보기'}
                className={state === 'ready' ? 'block w-full' : 'hidden'}
                onLoad={() => setState('ready')}
                onError={() => setState('error')}
              />
            ) : null}
            {state === 'loading' ? <p className="p-6 text-sm text-slate-500">이미지를 만드는 중입니다…</p> : null}
          </div>
          <div className="grid max-w-sm gap-3 text-sm text-slate-600">
            <a
              href={cardUrl(brandId, back, true)}
              className="inline-flex h-9 w-fit items-center justify-center rounded-md bg-slate-900 px-4 font-medium text-white hover:bg-slate-700"
            >
              PNG 다운로드
            </a>
            {cycle?.inProgress ? (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-amber-800">
                진행 중인 회차입니다. 언급률은 어제까지, 산출물은 오늘까지 반영됩니다.
              </p>
            ) : null}
            <p>0건인 항목은 이미지에서 빠집니다. 언급률은 고객 보고서와 같은 표시값이고, 표본 수는 싣지 않습니다.</p>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
